import { Fragment, type ReactNode } from 'react';

// Lightweight inline-markup helpers. Supported markup is <b>, <i>, <u> (bold/
// italic/underline, freely nestable), stored verbatim in the plain `text`
// strings — no schema/DB change required. Rendered on mobile by the matching
// apps/mobile/src/lib/richText.tsx.

export type RichTag = 'b' | 'i' | 'u';

const TAG_RE = /<\/?(b|i|u)>/g;

const UNDERLINE_SEGMENT = /(<u>[\s\S]*?<\/u>)/g;
const UNDERLINE_WRAP = /^<u>([\s\S]*)<\/u>$/;

/** Render a string that may contain <u>…</u> markup as React nodes with real <u> elements. */
export function renderUnderline(text?: string | null): ReactNode {
  const value = text ?? '';
  if (!value.includes('<u>')) return value;
  return value.split(UNDERLINE_SEGMENT).map((segment, i) => {
    const match = UNDERLINE_WRAP.exec(segment);
    return match ? <u key={i}>{match[1]}</u> : <Fragment key={i}>{segment}</Fragment>;
  });
}

/** Strip all <b>/<i>/<u> markup, leaving plain text. Use for table/list previews. */
export function stripUnderline(text?: string | null): string {
  return (text ?? '').replace(TAG_RE, '');
}

interface RichSegment {
  text: string;
  bold: boolean;
  italic: boolean;
  underline: boolean;
}

/** Split text into plain-text runs tagged with which of b/i/u (freely nested) apply. */
function parseRichSegments(text: string): RichSegment[] {
  const segments: RichSegment[] = [];
  let bold = false;
  let italic = false;
  let underline = false;
  let lastIndex = 0;
  TAG_RE.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = TAG_RE.exec(text))) {
    if (match.index > lastIndex) {
      segments.push({ text: text.slice(lastIndex, match.index), bold, italic, underline });
    }
    const closing = match[0][1] === '/';
    if (match[1] === 'b') bold = !closing;
    else if (match[1] === 'i') italic = !closing;
    else underline = !closing;
    lastIndex = TAG_RE.lastIndex;
  }
  if (lastIndex < text.length) {
    segments.push({ text: text.slice(lastIndex), bold, italic, underline });
  }
  return segments;
}

/** Render a string that may contain <b>/<i>/<u> markup as React nodes with real styling. */
export function renderRichText(text?: string | null): ReactNode {
  const value = text ?? '';
  if (!/[biu]>/.test(value)) return value;
  return parseRichSegments(value).map((seg, i) => (
    <span
      key={i}
      style={{
        fontWeight: seg.bold ? 700 : undefined,
        fontStyle: seg.italic ? 'italic' : undefined,
        textDecoration: seg.underline ? 'underline' : undefined,
      }}
    >
      {seg.text}
    </span>
  ));
}

/**
 * Toggle <tag>…</tag> markup around the [start, end) selection within `value`.
 * If the selection is already exactly wrapped, it is unwrapped. Returns the new
 * value plus the selection range to restore so the user keeps their highlight.
 */
export function toggleTag(
  tag: RichTag,
  value: string,
  start: number,
  end: number,
): { value: string; selectionStart: number; selectionEnd: number } {
  if (start === end) return { value, selectionStart: start, selectionEnd: end };

  const before = value.slice(0, start);
  const selected = value.slice(start, end);
  const after = value.slice(end);
  const wrap = new RegExp(`^<${tag}>([\\s\\S]*)<\\/${tag}>$`);
  const wrapMatch = wrap.exec(selected);

  if (wrapMatch) {
    return {
      value: before + wrapMatch[1] + after,
      selectionStart: start,
      selectionEnd: start + wrapMatch[1].length,
    };
  }

  const next = `${before}<${tag}>${selected}</${tag}>${after}`;
  return {
    value: next,
    selectionStart: start + tag.length + 2,
    selectionEnd: start + tag.length + 2 + selected.length,
  };
}

/** Toggle <u> markup around the [start, end) selection. Thin wrapper over toggleTag. */
export function toggleUnderline(value: string, start: number, end: number) {
  return toggleTag('u', value, start, end);
}
